/**
 * Development Helper
 * Provides quick authentication for testing (LOCAL DEV ONLY).
 *
 * SECURITY:
 * - This helper is hard-disabled outside `import.meta.env.DEV` builds.
 * - The previous `?test=true` URL bypass has been removed: it allowed the
 *   "Quick Test Login" UI and hardcoded credentials to surface on production
 *   builds, which is unacceptable.
 * - Test credentials are read from Vite env vars at build time and are
 *   stripped from the production bundle (the `if (!isDevelopment) return`
 *   guards short-circuit before any sensitive code runs).
 */

import { supabase } from "@/integrations/supabase/client";

// Credentials sourced from Vite env (only present in local .env files).
// Production builds will see empty strings here.
const DEV_TEST_EMAIL = (import.meta.env.VITE_DEV_TEST_EMAIL as string | undefined) ?? "";
const DEV_TEST_PASSWORD = (import.meta.env.VITE_DEV_TEST_PASSWORD as string | undefined) ?? "";

// Strict gate: localhost + Vite dev build only. No URL-based escape hatch.
const isLocalhost = (() => {
  try {
    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1' || host === '[::1]';
  } catch {
    return false;
  }
})();

export const isDevelopment = import.meta.env.DEV && isLocalhost;

/**
 * Quick login with test account (development only)
 * No-op outside local dev builds. Returns an error if credentials are not configured.
 */
export const quickDevLogin = async () => {
  if (!isDevelopment) {
    console.warn("Dev helper only available in development mode");
    return { error: new Error("Not in development mode") };
  }

  if (!DEV_TEST_EMAIL || !DEV_TEST_PASSWORD) {
    console.warn(
      "Dev login credentials not configured. Set VITE_DEV_TEST_EMAIL and VITE_DEV_TEST_PASSWORD in your local .env."
    );
    return { error: new Error("Dev credentials not configured") };
  }

  try {
    // Try to sign in first
    const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
      email: DEV_TEST_EMAIL,
      password: DEV_TEST_PASSWORD,
    });

    if (!signInError && signInData.user) {
      console.log("✅ Dev login successful:", DEV_TEST_EMAIL);
      return { data: signInData, error: null };
    }

    // If sign in failed, try to create account
    if (signInError?.message?.includes("Invalid login credentials")) {
      console.log("📝 Creating dev test account...");
      
      const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
        email: DEV_TEST_EMAIL,
        password: DEV_TEST_PASSWORD,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (signUpError) throw signUpError;

      console.log("✅ Dev account created and logged in:", DEV_TEST_EMAIL);
      return { data: signUpData, error: null };
    }

    throw signInError;
  } catch (error: any) {
    console.error("❌ Dev login failed:", error);
    return { error };
  }
};

/**
 * Get dev test account credentials (for display only)
 */
export const getDevCredentials = () => {
  if (!isDevelopment || !DEV_TEST_EMAIL || !DEV_TEST_PASSWORD) return null;
  
  return {
    email: DEV_TEST_EMAIL,
    password: DEV_TEST_PASSWORD,
  };
};

/**
 * Test badge IDs for testing badge unlock modal
 */
export const getTestBadgeIds = (count: 1 | 2 | 3 = 1): string[] => {
  const testBadges = ['first_expense', 'streak_3_days', 'coffee_lover'];
  return testBadges.slice(0, count);
};
